import React, { useState, useMemo } from "react";
import { ThemeProvider, CssBaseline, Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader, AppBar, Toolbar, Typography, IconButton, useMediaQuery, Chip, Divider } from "@mui/material";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import PeopleIcon from "@mui/icons-material/People";
import SpaIcon from "@mui/icons-material/Spa";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import BoltIcon from "@mui/icons-material/Bolt";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { lightTheme, darkTheme } from "./theme";
import { ProgrammeProfile } from "./pages/ProgrammeProfile";
import { WellnessOverview } from "./pages/WellnessOverview";
import { Recovery } from "./pages/Recovery";
import { SleepAnalytics } from "./pages/SleepAnalytics";
import { StrainActivity } from "./pages/StrainActivity";
import { StressMind } from "./pages/StressMind";
import { Biometrics } from "./pages/Biometrics";
import { UserProgress } from "./pages/UserProgress";
import { TeamProgress } from "./pages/TeamProgress";
import { JeopardyInsights } from "./pages/JeopardyInsights";
import { MeasureComparison } from "./pages/MeasureComparison";
import { PredictiveInsights } from "./pages/PredictiveInsights";
import { ManagementDashboard } from "./pages/ManagementDashboard";

const DRAWER_WIDTH = 280;

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactElement;
}

const WELLNESS_NAV: NavItem[] = [
  { path: "/programme-profile", label: "Programme Profile", icon: <PeopleIcon /> },
  { path: "/wellness-overview", label: "Wellness Overview", icon: <SpaIcon /> },
  { path: "/recovery", label: "Recovery", icon: <FavoriteIcon /> },
  { path: "/sleep", label: "Sleep Analytics", icon: <BedtimeIcon /> },
  { path: "/strain", label: "Strain & Activity", icon: <BoltIcon /> },
  { path: "/stress", label: "Physiological Stress", icon: <SelfImprovementIcon /> },
  { path: "/biometrics", label: "Biometrics", icon: <MonitorHeartIcon /> },
  { path: "/user-progress", label: "User Progress", icon: <PersonIcon /> },
  { path: "/team-progress", label: "Team Progress", icon: <GroupsIcon /> },
  { path: "/jeopardy", label: "Jeopardy Insights", icon: <WarningAmberIcon /> },
  { path: "/measure-comparison", label: "Measure Comparison", icon: <CompareArrowsIcon /> },
  { path: "/predictive", label: "Predictive Insights", icon: <TipsAndUpdatesIcon /> },
];

const ADMIN_NAV: NavItem[] = [
  { path: "/management", label: "Management Dashboard", icon: <DashboardIcon /> },
];

const ALL_NAV = [...WELLNESS_NAV, ...ADMIN_NAV];

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [mobileOpen, setMobileOpen] = useState(false);

  const renderNavItems = (items: NavItem[]) =>
    items.map((item) => (
      <ListItemButton
        key={item.path}
        selected={location.pathname === item.path}
        onClick={() => { navigate(item.path); setMobileOpen(false); }}
        sx={{
          mx: 1,
          borderRadius: 1,
          mb: 0.5,
          "&.Mui-selected": { backgroundColor: "action.selected" },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: location.pathname === item.path ? "primary.main" : "text.secondary" }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText primary={item.label} primaryTypographyProps={{ variant: "body2", fontWeight: location.pathname === item.path ? 600 : 400 }} />
      </ListItemButton>
    ));

  const drawerContent = (
    <Box sx={{ pt: 2, display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ px: 2, pb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" fontWeight={700} color="primary">Peak4</Typography>
        <Chip label="Demo" size="small" color="primary" variant="outlined" />
      </Box>
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <List
          subheader={
            <ListSubheader sx={{ bgcolor: "transparent", lineHeight: "32px", fontSize: "0.7rem", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "text.disabled" }}>
              Wellness & Health
            </ListSubheader>
          }
        >
          {renderNavItems(WELLNESS_NAV)}
        </List>
        <Divider sx={{ mx: 2, my: 1 }} />
        <List
          subheader={
            <ListSubheader sx={{ bgcolor: "transparent", lineHeight: "32px", fontSize: "0.7rem", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "text.disabled" }}>
              Administration
            </ListSubheader>
          }
        >
          {renderNavItems(ADMIN_NAV)}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH } }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            "& .MuiDrawer-paper": { width: DRAWER_WIDTH, borderRight: "1px solid", borderColor: "divider" },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
          <Toolbar>
            {isMobile && (
              <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="body1" fontWeight={600} sx={{ flex: 1 }}>
              {ALL_NAV.find((item) => item.path === location.pathname)?.label || "Dashboard"}
            </Typography>
            <Chip label="Simulated Data" size="small" variant="outlined" color="warning" sx={{ mr: 1 }} />
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, overflow: "auto", bgcolor: "background.default" }}>
          <Routes>
            <Route path="/programme-profile" element={<ProgrammeProfile />} />
            <Route path="/wellness-overview" element={<WellnessOverview />} />
            <Route path="/recovery" element={<Recovery />} />
            <Route path="/sleep" element={<SleepAnalytics />} />
            <Route path="/strain" element={<StrainActivity />} />
            <Route path="/stress" element={<StressMind />} />
            <Route path="/biometrics" element={<Biometrics />} />
            <Route path="/user-progress" element={<UserProgress />} />
            <Route path="/team-progress" element={<TeamProgress />} />
            <Route path="/jeopardy" element={<JeopardyInsights />} />
            <Route path="/measure-comparison" element={<MeasureComparison />} />
            <Route path="/predictive" element={<PredictiveInsights />} />
            <Route path="/management" element={<ManagementDashboard />} />
            <Route path="*" element={<Navigate to="/wellness-overview" replace />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

const App: React.FC = () => {
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const [darkMode, setDarkMode] = useState(prefersDark);
  const theme = useMemo(() => (darkMode ? darkTheme : lightTheme), [darkMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Box sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1300 }}>
          <IconButton onClick={() => setDarkMode(!darkMode)} sx={{ bgcolor: "background.paper", boxShadow: 2, "&:hover": { bgcolor: "action.hover" } }}>
            {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Box>
        <AppLayout />
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
